/**
 * Small square thumbnails of a reference sheet — Settings' child card and
 * the appearance screen's "Propositions précédentes" row — prefer the
 * backend's `GET .../sheets/{id}/portrait` (256x256 WebP, already centred
 * on the child's face) over the old `frontPoseCrop` CSS guess on top of the
 * full preview. The portrait endpoint is new: an older/not-yet-deployed
 * backend 404s on it, so `resolveChildSheetThumbnail` falls back to the
 * full preview (plus the CSS crop) whenever it fails, for any reason. Large
 * views (the chosen/candidate cards) keep using the full sheet unchanged —
 * see `useChildSheetImage` in `childSheetImage.ts`.
 */
import { useEffect, useState } from "react";
import { frontPoseCrop } from "./childSheets";
import { getChildSheetImageBlob, getChildSheetPortraitBlob } from "./dataSource";

/** CSS crop applied only to the fallback path (a full sheet preview, not a
 *  portrait): the portrait is already square and centred, so it needs none. */
export const THUMBNAIL_FALLBACK_CROP = frontPoseCrop();

export interface ThumbnailFetchers {
  getPortrait: (childId: string, sheetId: string) => Promise<Blob>;
  getFullImage: (childId: string, sheetId: string) => Promise<Blob>;
}

const defaultFetchers: ThumbnailFetchers = {
  getPortrait: getChildSheetPortraitBlob,
  getFullImage: getChildSheetImageBlob,
};

/**
 * Picks which image a thumbnail should show: the portrait when it can be
 * fetched, the full preview (flagged `cropped: true`, so the caller applies
 * `THUMBNAIL_FALLBACK_CROP`) when it can't — a 404 on an older backend, or
 * any other error. Pure apart from the two injected fetchers, so the
 * fallback behaviour is testable without a DOM or a real network call.
 */
export async function resolveChildSheetThumbnail(
  childId: string,
  sheetId: string,
  fetchers: ThumbnailFetchers = defaultFetchers,
): Promise<{ blob: Blob; cropped: boolean }> {
  try {
    const blob = await fetchers.getPortrait(childId, sheetId);
    return { blob, cropped: false };
  } catch {
    const blob = await fetchers.getFullImage(childId, sheetId);
    return { blob, cropped: true };
  }
}

export interface ChildSheetThumbnailState {
  url: string | null;
  /** CSS style to apply on top of `url` — `THUMBNAIL_FALLBACK_CROP` when
   *  this fell back to the full preview, `null` for a real portrait (or
   *  while loading/failed) which needs no offset. */
  crop: Record<string, string> | null;
}

/** `useChildSheetImage`'s sibling for thumbnails: an object URL plus the
 *  crop style to apply on top of it, if any. Revoked on unmount or when the
 *  sheet changes, same convention as `useChildSheetImage`. */
export function useChildSheetThumbnail(
  childId: string | null | undefined,
  sheetId: string | null | undefined,
): ChildSheetThumbnailState {
  const [state, setState] = useState<ChildSheetThumbnailState>({ url: null, crop: null });

  useEffect(() => {
    setState({ url: null, crop: null });
    if (!childId || !sheetId) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    resolveChildSheetThumbnail(childId, sheetId)
      .then(({ blob, cropped }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, crop: cropped ? THUMBNAIL_FALLBACK_CROP : null });
      })
      .catch(() => {
        // Both the portrait and the full-preview fallback failed (e.g. the
        // sheet itself is gone) — nothing to show, same as `useChildSheetImage`.
        if (!cancelled) setState({ url: null, crop: null });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [childId, sheetId]);

  return state;
}
